import { coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  HostBinding,
  Inject,
  InjectionToken,
  Input,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';

import { isThemeColor, ThemeColor } from '../../../types/theme';
import { ThemeColorService } from '../../services/theme-color.service';

const BASE_SIZE = 100;
const BASE_STROKE_WIDTH = 10;

type Mode = 'determinate' | 'indeterminate';

const INDETERMINATE_ANIMATION_TEMPLATE = `
 @keyframes circular-progress-indeterminate-DIAMETER {
    0%      { stroke-dashoffset: START_VALUE;  transform: rotate(0); }
    12.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(0); }
    12.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(72.5deg); }
    25%     { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(72.5deg); }
    25.0001%   { stroke-dashoffset: START_VALUE;  transform: rotate(270deg); }
    37.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(270deg); }
    37.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(161.5deg); }
    50%     { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(161.5deg); }
    50.0001%  { stroke-dashoffset: START_VALUE;  transform: rotate(180deg); }
    62.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(180deg); }
    62.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(251.5deg); }
    75%     { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(251.5deg); }
    75.0001%  { stroke-dashoffset: START_VALUE;  transform: rotate(90deg); }
    87.5%   { stroke-dashoffset: END_VALUE;    transform: rotate(90deg); }
    87.5001%  { stroke-dashoffset: END_VALUE;    transform: rotateX(180deg) rotate(341.5deg); }
    100%    { stroke-dashoffset: START_VALUE;  transform: rotateX(180deg) rotate(341.5deg); }
  }
`;

export interface CircularProgressDefaultOptions {
  /** Default color of the spinner. */
  color?: ThemeColor;
  /** Diameter of the spinner. */
  diameter?: number;
  /** Width of the spinner's stroke. */
  strokeWidth?: number;
}

/** Injection token to be used to override the default options for `lui-circular-progress`. */
export const CIRCULAR_PROGRESS_DEFAULT_OPTIONS =
  new InjectionToken<CircularProgressDefaultOptions>(
    'circular-progress-default-options',
    {
      providedIn: 'root',
      factory: CIRCULAR_PROGRESS_DEFAULT_OPTIONS_FACTORY,
    }
  );

export function CIRCULAR_PROGRESS_DEFAULT_OPTIONS_FACTORY(): CircularProgressDefaultOptions {
  return { diameter: BASE_SIZE };
}

@Component({
    selector: 'lui-circular-progress',
    templateUrl: './circular-progress.component.html',
    standalone: false
})
export class CircularProgressComponent implements OnInit {
  /**
   * Tracks diameters of existing instances to de-dupe generated styles (default d = 100).
   * We need to keep track of which elements the diameters were attached to, because for
   * elements in the Shadow DOM the style tags are attached to the shadow root, rather
   * than the document head.
   */
  private static _diameters = new WeakMap<Node, Set<number>>();

  private _model = 'circular-progress';
  private _diameter = BASE_SIZE;
  private _value = 0;
  private _strokeWidth!: number;

  @ViewChild('progressBar', { static: true })
  progressBar!: ElementRef<SVGPathElement>;

  strokeDashOffset!: number;

  @Input() get value(): number {
    return this.mode === 'determinate' ? this._value : 0;
  }
  set value(newValue: NumberInput) {
    this._value = Math.max(0, Math.min(100, coerceNumberProperty(newValue)));
  }

  /** The diameter of the progress spinner (will set width and height of svg). */
  @Input()
  get diameter(): number {
    return this._diameter;
  }
  set diameter(size: NumberInput) {
    this._diameter = coerceNumberProperty(size);
    this._spinnerAnimationLabel = this._getSpinnerAnimationLabel();

    // If this is set before `ngOnInit`, the style root may not have been resolved yet.
    if (this._styleRoot) {
      this._attachStyleNode();
    }
  }

  /** Stroke width of the progress spinner. */
  @Input()
  get strokeWidth(): number {
    return this._strokeWidth || this.diameter / 10;
  }
  set strokeWidth(value: NumberInput) {
    this._strokeWidth = coerceNumberProperty(value);
  }

  @Input() get color(): ThemeColor | 'default' {
    return this._color;
  }
  set color(value: string) {
    if (isThemeColor(value) || value === 'default') {
      this._color = value;
    }
  }
  private _color: ThemeColor | 'default' = 'default';

  @Input() mode: Mode = 'determinate';

  @HostBinding('class') get classNames() {
    return {
      [this._model]: true,
      [`${this._model}-determinate`]: this.mode === 'determinate',
      [`${this._model}-indeterminate`]: this.mode === 'indeterminate',
      ...this.themeColorService.generateClasses(this._model, this.color),
    };
  }

  @HostBinding('style.width') get width() {
    return `${this.diameter}px`;
  }

  @HostBinding('style.height') get height() {
    return `${this.diameter}px`;
  }

  /**
   * Element to which we should add the generated style tags for the indeterminate animation.
   * For most elements this is the document, but for the ones in the Shadow DOM we need to
   * use the shadow root.
   */
  private _styleRoot!: Node;

  /** A string that is used for setting the spinner animation-name CSS property */
  _spinnerAnimationLabel: string;

  constructor(
    private element: ElementRef,
    private themeColorService: ThemeColorService,
    @Optional() @Inject(DOCUMENT) private _document: Document,
    @Inject(CIRCULAR_PROGRESS_DEFAULT_OPTIONS)
    defaults?: CircularProgressDefaultOptions
  ) {
    const trackedDiameters = CircularProgressComponent._diameters;
    this._spinnerAnimationLabel = this._getSpinnerAnimationLabel();

    // The base size is already inserted via the component's structural styles. We still
    // need to track it so we don't end up adding the same styles again.
    if (!trackedDiameters.has(_document.head)) {
      trackedDiameters.set(_document.head, new Set<number>([BASE_SIZE]));
    }

    if (defaults) {
      if (defaults.color) {
        this.color = defaults.color;
      }

      if (defaults.diameter) {
        this.diameter = defaults.diameter;
      }

      if (defaults.strokeWidth) {
        this.strokeWidth = defaults.strokeWidth;
      }
    }
  }

  ngOnInit(): void {
    // Note that we need to look up the root node in ngOnInit, rather than the constructor, because
    // Angular seems to create the element outside the shadow root and then moves it inside, if the
    // node is inside an `ngIf` and a ShadowDom-encapsulated component.
    this._styleRoot = this._document.head;
    this._attachStyleNode();

    setTimeout(() => {
      this.element.nativeElement.classList.add(
        'circular-progress-animation-ready'
      );
    }, 5);
  }

  /** The radius of the spinner, adjusted for stroke width. */
  _getCircleRadius() {
    return (this.diameter - BASE_STROKE_WIDTH) / 2;
  }

  /** The view box of the spinner's svg element. */
  _getViewBox() {
    const viewBox = this._getCircleRadius() * 2 + this.strokeWidth;
    return `0 0 ${viewBox} ${viewBox}`;
  }

  /** The stroke circumference of the svg circle. */
  _getStrokeCircumference(): number {
    return 2 * Math.PI * this._getCircleRadius();
  }

  /** The dash offset of the svg circle. */
  _getStrokeDashOffset() {
    if (this.mode === 'determinate') {
      return (this._getStrokeCircumference() * (100 - this.value)) / 100;
    }

    return null;
  }

  /** Gets the `transform-origin` for the inner circle element. */
  _getCircleTransformOrigin(svg: HTMLElement): string {
    // Safari has an issue where the `transform-origin` doesn't work as expected when the page
    // has a different zoom level from the default. The problem appears to be that a zoom
    // is applied on the `svg` node itself. We can work around it by calculating the origin
    // based on the zoom level. On all other browsers the `currentScale` appears to always be 1.
    const scale = ((svg as unknown as SVGSVGElement).currentScale ?? 1) * 50;
    return `${scale}% ${scale}%`;
  }

  /** Stroke width of the circle in percent. */
  _getCircleStrokeWidth() {
    return (this.strokeWidth / this.diameter) * 100;
  }

  /** Dynamically generates a style tag containing the correct animation for this diameter. */
  private _attachStyleNode(): void {
    const styleRoot = this._styleRoot;
    const currentDiameter = this._diameter;
    const diameters = CircularProgressComponent._diameters;
    let diametersForElement = diameters.get(styleRoot);

    if (!diametersForElement || !diametersForElement.has(currentDiameter)) {
      const styleTag: HTMLStyleElement = this._document.createElement('style');
      styleTag.setAttribute(
        'circular-progress-animation',
        this._spinnerAnimationLabel
      );
      styleTag.textContent = this._getAnimationText();
      styleRoot.appendChild(styleTag);

      if (!diametersForElement) {
        diametersForElement = new Set<number>();
        diameters.set(styleRoot, diametersForElement);
      }

      diametersForElement.add(currentDiameter);
    }
  }

  /** Generates animation styles adjusted for the spinner's diameter. */
  private _getAnimationText(): string {
    const strokeCircumference = this._getStrokeCircumference();
    return (
      INDETERMINATE_ANIMATION_TEMPLATE
        // Animation should begin at 5% and end at 80%
        .replace(/START_VALUE/g, `${0.95 * strokeCircumference}`)
        .replace(/END_VALUE/g, `${0.2 * strokeCircumference}`)
        .replace(/DIAMETER/g, `${this._spinnerAnimationLabel}`)
    );
  }

  /** Returns the circle diameter formatted for use with the animation-name CSS property. */
  private _getSpinnerAnimationLabel(): string {
    // The string of a float point number will include a period ‘.’ character,
    // which is not valid for a CSS animation-name.
    return this.diameter.toString().replace('.', '_');
  }
}
